// Image compression using pica

// Initialize pica resizer
let picaInstance = null;

function initPica() {
  if (!picaInstance) {
    picaInstance = window.pica();
  }
  return picaInstance;
}

const MAX_CANVAS_PIXELS = 16_000_000;

function isHeicFile(file) {
  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return /heic|heif/.test(mime) || /\.(heic|heif)$/.test(name);
}

// Detect if a file should be treated as an image (handles empty MIME for HEIC)
function isImageFile(file) {
  if (file.type && file.type.startsWith('image/')) return true;
  return isHeicFile(file);
}

// Cache the initialized libheif WASM module (factory must be called once)
let libheifInstance = null;
async function getLibheif() {
  if (!libheifInstance) {
    libheifInstance = await libheif();
  }
  return libheifInstance;
}

// Convert HEIC to JPEG using libheif-js (WASM-based decoder)
function convertHeicToJpeg(file) {
  return new Promise(async (resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('HEIC conversion timed out'));
    }, 120_000);

    try {
      const buffer = await file.arrayBuffer();
      const heif = await getLibheif();
      const decoder = new heif.HeifDecoder();
      const data = decoder.decode(new Uint8Array(buffer));

      if (!data || !data.length) {
        throw new Error('No images found in HEIC file');
      }

      const image = data[0];
      const width = image.get_width();
      const height = image.get_height();

      // Decode at full resolution — image.display() always writes at original dimensions.
      const decodeCanvas = document.createElement('canvas');
      decodeCanvas.width = width;
      decodeCanvas.height = height;
      const decodeCtx = decodeCanvas.getContext('2d');
      const imageData = decodeCtx.createImageData(width, height);

      await new Promise((res, rej) => {
        image.display(imageData, (displayData) => {
          if (!displayData) {
            return rej(new Error('HEIF processing error'));
          }
          res();
        });
      });

      decodeCtx.putImageData(imageData, 0, 0);

      // If the decoded image exceeds canvas limits, draw it scaled down onto a smaller canvas
      const clamped = clampToCanvasLimit(width, height);
      let canvas;
      if (clamped.width !== width || clamped.height !== height) {
        canvas = document.createElement('canvas');
        canvas.width = clamped.width;
        canvas.height = clamped.height;
        canvas.getContext('2d').drawImage(decodeCanvas, 0, 0, clamped.width, clamped.height);
      } else {
        canvas = decodeCanvas;
      }

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.8));

      if (!blob) {
        throw new Error('canvas.toBlob returned null');
      }

      const jpegFile = new File(
        [blob],
        file.name.replace(/\.(heic|heif)$/i, '.jpg'),
        { type: 'image/jpeg', lastModified: file.lastModified }
      );
      clearTimeout(timeout);
      resolve(jpegFile);
    } catch (err) {
      clearTimeout(timeout);
      reject(err);
    }
  });
}

// Clamp source dimensions so total pixels stay within canvas limits
function clampToCanvasLimit(width, height) {
  const pixels = width * height;
  if (pixels <= MAX_CANVAS_PIXELS) return { width, height };
  const scale = Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  return {
    width: Math.floor(width * scale),
    height: Math.floor(height * scale)
  };
}

// Try to decode a file natively via the browser (works for HEIC on iOS Safari)
function tryNativeDecode(file, objectUrl) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('Browser cannot decode this image'));
    img.src = objectUrl;
  });
}

// Compress and resize image using pica
export async function compressImage(file) {
  const isHeic = isHeicFile(file);

  // Small non-HEIC images: skip compression to keep uploads fast
  if (!isHeic && file.size && file.size < 300 * 1024) {
    return file;
  }

  // Use createObjectURL instead of readAsDataURL to avoid huge base64 strings
  const objectUrl = URL.createObjectURL(file);

  try {
    let sourceBitmap;

    if (isHeic) {
      // First try native browser decoding (iOS Safari supports HEIC natively)
      try {
        sourceBitmap = await tryNativeDecode(file, objectUrl);
      } catch (_nativeErr) {
        // Native decode failed — fall back to libheif WASM conversion
        if (window.libheif) {
          try {
            const jpegFile = await convertHeicToJpeg(file);
            URL.revokeObjectURL(objectUrl);
            return compressImage(jpegFile);
          } catch (libheifErr) {
            console.warn('libheif failed, trying heic2any:', libheifErr.message);
          }
        }
        // Fall back to heic2any
        if (typeof heic2any !== 'undefined') {
          try {
            const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
            const jpegFile = new File(
              [convertedBlob],
              file.name.replace(/\.(heic|heif)$/i, '.jpg'),
              { type: 'image/jpeg', lastModified: file.lastModified }
            );
            URL.revokeObjectURL(objectUrl);
            return compressImage(jpegFile);
          } catch (heic2anyErr) {
            window.rollbar.error('HEIC conversion failed (both libheif and heic2any):', heic2anyErr);
          }
        }
        return file;
      }
    } else {
      sourceBitmap = await tryNativeDecode(file, objectUrl);
    }

    const srcWidth = sourceBitmap.width;
    const srcHeight = sourceBitmap.height;

    // Clamp source dimensions to stay within browser canvas limits
    const clamped = clampToCanvasLimit(srcWidth, srcHeight);

    // Create source canvas (possibly downscaled to fit canvas limit)
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = clamped.width;
    sourceCanvas.height = clamped.height;
    const sourceCtx = sourceCanvas.getContext('2d');
    sourceCtx.drawImage(sourceBitmap, 0, 0, clamped.width, clamped.height);

    // Close bitmap to free memory immediately
    if (sourceBitmap.close) sourceBitmap.close();

    // Calculate target dimensions (max 1600px on longest side)
    let targetWidth = clamped.width;
    let targetHeight = clamped.height;
    const MAX_SIZE = 1600;
    if (clamped.width > MAX_SIZE || clamped.height > MAX_SIZE) {
      if (clamped.width > clamped.height) {
        targetWidth = MAX_SIZE;
        targetHeight = Math.round(clamped.height * (MAX_SIZE / clamped.width));
      } else {
        targetHeight = MAX_SIZE;
        targetWidth = Math.round(clamped.width * (MAX_SIZE / clamped.height));
      }
    }

    const targetCanvas = document.createElement('canvas');
    targetCanvas.width = targetWidth;
    targetCanvas.height = targetHeight;

    const pica = initPica();
    const result = await pica.resize(sourceCanvas, targetCanvas, {
      unsharpAmount: 160,
      unsharpRadius: 0.6,
      unsharpThreshold: 1
    });

    // Always output as JPEG for consistency and smaller size
    const outputType = (file.type && file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif')
      ? file.type
      : 'image/jpeg';
    const blob = await pica.toBlob(result, outputType, 0.85);

    const finalFile = new File([blob], file.name, {
      type: outputType,
      lastModified: file.lastModified
    });
    return finalFile;
  } catch (err) {
    window.rollbar.error('Error during image processing:', err);
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Handle image selection and compression
export async function handleImageSelect(input) {
  if (input.files && input.files.length > 0) {
    const loadingSpinner = document.querySelector('.loading-spinner');
    if (loadingSpinner) loadingSpinner.style.display = 'block';

    try {
      initPica();

      const files = Array.from(input.files);
      // Process all images in parallel for speed
      const processedFiles = await Promise.all(
        files.map(file =>
          isImageFile(file) ? compressImage(file) : Promise.resolve(file)
        )
      );

      const dataTransfer = new DataTransfer();
      processedFiles.forEach(file => dataTransfer.items.add(file));
      input.files = dataTransfer.files;
      input.closest('form').submit();
    } catch (error) {
      window.rollbar.error('Error processing images:', error);
      input.closest('form').submit();
    }
  }
}

// Expose functions globally for direct use in HTML script tags
window.compressImage = compressImage;
